import { useState, useRef, useCallback, useEffect } from 'react';
import { Message } from '../types/chat';
import { useWebSocket } from './useWebSocket';
import { useMessageWindow } from './useMessageWindow';
import api, { getApiBaseUrl } from '../utils/axios';
import { generateMessageId } from '../utils/messageIdGenerator';
import { versionControlApi } from '../services/versionControlApi';

// How long the UI shows a run as stopping before it stops waiting for the run's own
// end event, which never comes if the backend worker running it died.
const STOPPING_TIMEOUT_MS = 60_000;

const COMMIT_TAG_RE = /\[COMMIT:\s*.+?\]/g;
const stripCommitTag = (content: string): string => content.replace(COMMIT_TAG_RE, '').trimEnd();

// Convert backend StreamingEvent messages to friendly frontend messages
const getFriendlyEventMessage = (message: any): string => {
  const { type, data } = message;
  
  // Check if we have the new structured friendly_message object
  if (data && data.friendly_message) {
    const friendlyMsg = data.friendly_message;
    
    // The message field already contains the appropriate message (start or complete)
    // based on the status field sent from backend
    let displayMessage = friendlyMsg.message || '';
    
    if (friendlyMsg.file) {
      displayMessage = displayMessage ? `${displayMessage} ${friendlyMsg.file}` : friendlyMsg.file;
    }
    
    return displayMessage.trim();
  }
  
  // Fallback: Check for old-style message field
  if (data && data.message && data.message.trim()) {
    return data.message;
  }
  
    return '';
};

// Ids minted by the backend are Mongo ObjectIds; optimistic/thinking entries
// use temp ids that must never be sent as pagination cursors
const PERSISTED_ID_RE = /^[0-9a-fA-F]{24}$/;

// Convert a raw chat-history API message into the frontend Message shape
const mapRawDevMessage = (msg: any): Message => ({
  id: msg._id || msg.id,
  role: msg.role,
  content: msg.content,
  timestamp: typeof msg.timestamp === 'number' ? new Date(msg.timestamp * 1000) : new Date(msg.timestamp),
  images: msg.images || msg.image_urls || [],  // Backward compatibility
  imageIds: msg.image_ids || [],  // New field for image IDs
  agentLogs: msg.agent_logs || [],
  toolExecutions: msg.tool_executions || [],
  isThinking: msg.is_thinking || false,
  thinkingMessage: msg.thinking_message || '',
  // Attachment data
  fileIds: msg.file_ids || [],
  artifactIds: msg.artifact_ids || [],
  fileNames: msg.file_names || [],
  artifactNames: msg.artifact_names || [],
  knowledgeDocId: msg.knowledge_doc_id,
  attachments: msg.attachments || [],  // Unified attachments array with source information
  tokenUsage: msg.token_usage ? {
    inputTokens: msg.token_usage.input_tokens || 0,
    outputTokens: msg.token_usage.output_tokens || 0,
    cacheCreationInputTokens: msg.token_usage.cache_creation_input_tokens || 0,
    cacheReadInputTokens: msg.token_usage.cache_read_input_tokens || 0,
    serviceTier: msg.token_usage.service_tier || 'standard',
    costs: msg.token_usage.costs
  } : undefined,
  // Development timing data
  developmentTiming: msg.development_timing,
  messageType: msg.message_type ? msg.message_type.toUpperCase() : undefined,
  user_info: msg.user_info,
  model_preference: msg.model_preference,
  // Version control fields
  segmentId: msg.segment_id,
  gitCommitHash: msg.git_commit_hash,
  checkpointTitle: msg.checkpoint_title,
  type: (msg.type === 'rollback_marker' || msg.message_type === 'ROLLBACK_MARKER') ? 'rollback_marker' : 'message',
  parentSegmentId: msg.parent_segment_id || msg.rollback_metadata?.parent_segment_id,
  forkCheckpointMessageId: msg.fork_checkpoint_message_id || msg.rollback_metadata?.fork_checkpoint_message_id,
});

export interface UseDevAgentOptions {
  projectId: string;
  onMessageUpdate?: (messages: Message[]) => void;
  onProjectStatsUpdate?: (projectId: string, tokenStats: any) => void;
  onNewMessageWithUrls?: (url?: string) => void;
  onRawWebSocketMessage?: (message: any) => void;
}

export interface ConversationState {
  thread_id: string;
  execution_status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'STOPPED_BY_USER' | 'DISCARDED';
  context?: any;
  last_activity: string;
}

export interface ThreadStatus {
  is_active: boolean;
  execution_status: string;
  last_activity?: string;
}

export type WaitingState = 'none' | 'waiting_for_updates' | 'show_resume_discard';

export function useDevAgent({ projectId, onMessageUpdate, onProjectStatsUpdate, onNewMessageWithUrls, onRawWebSocketMessage }: UseDevAgentOptions) {
  // State management
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  // Explicit view state. 'live': the list ends at the thread's true tail, so
  // streaming appends and auto-scroll are valid. 'window': a historical slice
  // opened by a navigator jump — newer messages exist past newestLoadedTs.
  const [chatView, setChatView] = useState<
    { mode: 'live' } | { mode: 'window'; newestLoadedTs: number }
  >({ mode: 'live' });
  // Live mirror of `messages` for async flows that must not read stale closures
  const messagesRef = useRef<Message[]>([]);
  // Version control: "View Original Chat". The segment gets its own paginated
  // window (useMessageWindow below), displayed instead of `messages`, which
  // stays untouched — streaming/WS only ever mutate the active `messages`.
  const [viewingSegmentId, setViewingSegmentId] = useState<string | null>(null);
  const viewingSegmentIdRef = useRef<string | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Append guard: the many WS handlers all append via setMessages; while a
  // historical window is shown, strip anything past its newest message —
  // appends there would fake adjacency with the thread's true tail.
  useEffect(() => {
    if (chatView.mode !== 'window') return;
    const ceiling = chatView.newestLoadedTs + 0.001;
    setMessages(prev => {
      const inWindow = prev.filter(
        m => new Date(m.timestamp as any).getTime() / 1000 <= ceiling
      );
      return inWindow.length === prev.length ? prev : inWindow;
    });
  }, [messages, chatView]);
  
  // Credential management state
  const [credentialCaptureRequest, setCredentialCaptureRequest] = useState<{
    requestId: string;
    parameters: any[];
    envFilePath: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  // Live mirror so load flows can check streaming state without stale closures
  const isStreamingRef = useRef(false);
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Stop reached a backend worker that is not running this conversation: it could
  // only flag the stop, and the run ends at its next check. Until then the run keeps
  // showing as running, with a spinner on Stop, and a message sent is queued behind it.
  const [isStopping, setIsStopping] = useState(false);
  const isStoppingRef = useRef(false);
  const stoppingTimeoutRef = useRef<number | null>(null);

  // Turns the spinner off once the run has really ended. The chat itself is updated by
  // the run's own end events, as for any run.
  const endStopping = useCallback(() => {
    if (!isStoppingRef.current) return;
    isStoppingRef.current = false;
    setIsStopping(false);
    if (stoppingTimeoutRef.current) {
      window.clearTimeout(stoppingTimeoutRef.current);
      stoppingTimeoutRef.current = null;
    }
  }, []);

  const [revertStatus, setRevertStatus] = useState<string | null>(null);

  // Scenario item type for tracking test progress
  interface ScenarioItem {
    index: number;
    name: string;
    status: 'pending' | 'running' | 'passed' | 'failed';
  }

  // Batch item type for test plan
  interface BatchItem {
    batch_index: number;
    batch_name: string;
    scenarios: ScenarioItem[];
  }

  // Live test streaming state
  const [liveTestState, setLiveTestState] = useState<{
    isLive: boolean;
    isBatchInitializing: boolean;
    recordingPath: string | null;
    currentAction: string;
    currentScenario: string | null;  // Current test scenario being tested
    batchName: string | null;  // Name of the current batch being tested (used as video title)
    currentBatchIndex: number;  // Current batch being tested (0-based)
    projectId: string | null;
    messageId: string | null;
    liveFrame: string | null;  // Base64 frame data for real-time preview
    scenarios: ScenarioItem[];  // List of scenarios for current batch (legacy)
    testPlan: BatchItem[];  // Full test plan with all batches
  }>({
    isLive: false,
    isBatchInitializing: false,
    recordingPath: null,
    currentAction: '',
    currentScenario: null,
    batchName: null,
    currentBatchIndex: 0,
    projectId: null,
    messageId: null,
    liveFrame: null,
    scenarios: [],
    testPlan: [],
  });


  // New states for reconnection flow
  const [waitingState, setWaitingState] = useState<WaitingState>('none');
  const [isCheckingState, setIsCheckingState] = useState(false);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState<number>(Date.now());
  const [currentConversationState, setCurrentConversationState] = useState<ConversationState | null>(null);
  
  // Timers and refs
  const waitingTimeoutRef = useRef<number | null>(null);
  const messageTimeoutRef = useRef<number | null>(null);
  const timeoutIdRef = useRef<number>(0); // Unique ID to track valid timeouts
  const isLoadingInitialMessagesRef = useRef(false);
  const currentThreadIdRef = useRef<string | undefined>(currentThreadId);
  
  // WebSocket hook
  const webSocket = useWebSocket();
  
  // Keep ref in sync with currentThreadId state
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId;
  }, [currentThreadId]);
  
  // Simple function to update thinking message
  const updateThinkingMessage = useCallback((newMessage: string) => {
    // Ignore blank/empty messages
    if (!newMessage || newMessage.trim() === '') {
      return;
    }

    setMessages(prev => {
      // Find existing thinking message
      const existingThinking = prev.find(msg => msg.id === 'agent-thinking');

      if (existingThinking) {
        // Update existing thinking message
        return prev.map(msg =>
          msg.id === 'agent-thinking'
            ? { ...msg, thinkingMessage: newMessage, timestamp: new Date() }
            : msg
        );
      } else {
        // Create new thinking message
        const thinkingMessage: Message = {
          id: 'agent-thinking',
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isThinking: true,
          thinkingMessage: newMessage
        };
        const filtered = prev.filter(msg => msg.id !== 'processing');
        return [...filtered, thinkingMessage];
      }
    });
  }, []);
  
  // Check conversation state and thread status
  const checkConversationState = useCallback(async (threadId: string): Promise<{
    conversationState: ConversationState | null;
    threadStatus: ThreadStatus | null;
  }> => {
    try {
      // Check both conversation state and thread status in parallel
      const [conversationResponse, threadStatusResponse] = await Promise.allSettled([
        api.get(`/dev-agent/projects/${projectId}/incomplete-conversations`),
        api.get(`/dev-agent/projects/${projectId}/conversations/${threadId}/status`)
      ]);

      let conversationState: ConversationState | null = null;
      if (conversationResponse.status === 'fulfilled') {
        const incompleteConversations = conversationResponse.value.data.incomplete_conversations || [];
        conversationState = incompleteConversations.find((conv: ConversationState) => conv.thread_id === threadId) || null;
      } else {
        console.log('No incomplete conversations found (normal for new threads)');
      }

      let threadStatus: ThreadStatus | null = null;
      if (threadStatusResponse.status === 'fulfilled') {
        threadStatus = threadStatusResponse.value.data;
      } else {
        console.log('No thread status found (normal for new threads)');
      }

      return { conversationState, threadStatus };
    } catch (error) {
      console.log('Error checking conversation state (normal for new threads):', error);
      return { conversationState: null, threadStatus: null };
    }
  }, [projectId]);

  // Add waiting message to the message list - use same ID as updateThinkingMessage
  const addWaitingMessage = useCallback(() => {
    const waitingMessage: Message = {
      id: 'agent-thinking',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isThinking: true,
      thinkingMessage: 'Working on your request...'
    };

    setMessages(prev => {
      // Remove existing thinking message if any
      const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
      return [...filtered, waitingMessage];
    });
  }, []);

  // Remove waiting message from the message list - use same ID as updateThinkingMessage
  const removeWaitingMessage = useCallback(() => {
    setMessages(prev => prev.filter(msg => msg.id !== 'agent-thinking'));
  }, []);

  // Load messages for a thread — uses versioned /chat-history endpoint for initial/refresh loads
  const loadMessages = useCallback(async (threadId: string, force = false, limit = 20, beforeMessageId?: string): Promise<{ messages: Message[]; hasMore: boolean } | null> => {
    if (isLoadingInitialMessagesRef.current) return null;

    console.log('📥 LOAD_MESSAGES: Called with threadId:', threadId, 'force:', force);
    console.log('📥 LOAD_MESSAGES: Current state - isLoading:', isLoading, 'isStreaming:', isStreaming, 'messages.length:', messages.length);

    // Skip if already loading to prevent duplicate requests
    if (!force && isLoadingInitialMessagesRef.current) {
      console.log('📥 LOAD_MESSAGES: Skipping - already loading');
      return null;
    }
    
    try {
      isLoadingInitialMessagesRef.current = true;
      console.log('📥 LOAD_MESSAGES: Loading messages for thread:', threadId);
      
      const response = {
        data: await versionControlApi.getVersionedChatHistory(projectId, threadId, limit, beforeMessageId),
      };
      
      console.log('📥 RAW API RESPONSE:', {
        messagesCount: response.data.messages?.length || 0,
        pagination: response.data.pagination,
      });
      
      // Update hasMore state based on pagination metadata
      const hasMore = response.data.pagination?.has_more || false;
      setHasMoreMessages(hasMore);
      
      // Ensure we have the expected response structure
      if (!response.data || !Array.isArray(response.data.messages)) {
        console.error('❌ Invalid API response structure:', response.data);
        throw new Error('Invalid API response structure');
      }

      const threadMessages = (response.data.messages || []).map(mapRawDevMessage);
      
      console.log('📥 LOAD_MESSAGES: Loaded', threadMessages.length, 'messages from server');
      
      // Handle pagination: if beforeMessageId is provided, prepend to existing messages
      if (beforeMessageId) {
        // MUST be a functional update: sequential pagination calls (e.g. the
        // navigator's jump loop) reuse one closure, so reading `messages` here
        // would spread a stale array and silently drop previously loaded batches.
        setMessages(prev => {
          if (prev.length === 0) {
            queueMicrotask(() => onMessageUpdate?.(threadMessages));
            return threadMessages;
          }
          const batchIds = new Set(threadMessages.map((m: Message) => m.id));
          const combinedMessages = [
            ...threadMessages,
            ...prev.filter(m => !batchIds.has(m.id)),
          ];
          queueMicrotask(() => onMessageUpdate?.(combinedMessages));
          return combinedMessages;
        });
      } else {
        // This is an initial load or refresh - replace all messages.
        // A plain load always lands on the latest page — back to the live view.
        setMessages(threadMessages);
        onMessageUpdate?.(threadMessages);
        setChatView({ mode: 'live' });
        // The server list never contains the transient thinking placeholder —
        // restore it if the agent is still working (e.g. user returned to the
        // latest page mid-stream via the FAB or the navigator)
        if (isStreamingRef.current) {
          addWaitingMessage();
        }
      }
      
      if (threadMessages.length > 0) {
        const lastMessage = threadMessages[threadMessages.length - 1];
        setLastMessageTimestamp(lastMessage.timestamp.getTime());
      }

      // Return the batch (chronological, oldest first) so callers that need a
      // deterministic pagination cursor don't have to read async React state
      return { messages: threadMessages, hasMore };
    } catch (error: any) {
      console.error('❌ LOAD_MESSAGES ERROR:', error);
      
      // Check if it's a 404 (thread not found) vs other errors
      if (error?.response?.status === 404) {
        console.log('📭 Thread not found (normal for new threads)');
        setMessages([]); // Empty messages for new threads
      } else if (error?.response?.status === 500) {
        console.error('🔥 Server error loading messages:', error?.response?.data);
        setMessages([]); // Don't crash the UI, but log the error
      } else {
        console.error('🔥 Unknown error loading messages:', error);
        setMessages([]); // Don't crash the UI, but log the error
      }
      return null;
    } finally {
      isLoadingInitialMessagesRef.current = false;
    }
  }, [onMessageUpdate, addWaitingMessage]);

  // "View Original Chat" runs on its own paginated window (useMessageWindow),
  // scoped to the segment endpoint. Reads the segment id from a ref so the
  // fetch identity stays stable across a segment switch (loadSegmentMessages
  // sets both the state and the ref).
  const fetchSegmentPage = useCallback(({ limit, beforeMessageId, afterMessageId }: {
    limit: number; beforeMessageId?: string; afterMessageId?: string;
  }) => {
    const segId = viewingSegmentIdRef.current;
    if (!projectId || !currentThreadId || !segId) {
      return Promise.resolve({ messages: [] });
    }
    return versionControlApi.getSegmentMessages(
      projectId, currentThreadId, segId, limit, beforeMessageId, afterMessageId,
    );
  }, [projectId, currentThreadId]);

  const segmentWindow = useMessageWindow(fetchSegmentPage, mapRawDevMessage);

  // Enter "View Original Chat": scope the segment window and load its latest
  // page. The active branch's `messages` are left untouched.
  const loadSegmentMessages = useCallback(async (segmentId: string) => {
    if (!projectId || !currentThreadId) return;
    setViewingSegmentId(segmentId);
    viewingSegmentIdRef.current = segmentId;
    await segmentWindow.loadLatest(20);
  }, [projectId, currentThreadId, segmentWindow]);

  const clearViewingSegment = useCallback(() => {
    setViewingSegmentId(null);
    viewingSegmentIdRef.current = null;
    segmentWindow.reset();
  }, [segmentWindow]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async (threadId: string) => {
    if (!messages.length) return;
    
    const oldestMessage = messages[0];

    try {
      await loadMessages(threadId, true, 20, oldestMessage.id);
    } catch (error) {
      console.error('❌ Error loading more messages:', error);
    }
  }, [messages, loadMessages]);

  // Shared tail handling for window loads: record the window's newest edge,
  // or — when the server says nothing newer exists — return to the live view,
  // restoring the thinking placeholder if the agent is still working
  const applyNewerEdge = useCallback((batch: Message[], hasMoreNewer: boolean) => {
    if (hasMoreNewer && batch.length > 0) {
      setChatView({
        mode: 'window',
        newestLoadedTs: new Date(batch[batch.length - 1].timestamp as any).getTime() / 1000,
      });
    } else {
      setChatView({ mode: 'live' });
      if (isStreamingRef.current) addWaitingMessage();
    }
  }, [addWaitingMessage]);

  // Navigator jump: load a window that starts AT the target message and runs
  // newer — after clicking a prompt, the thing to read is the answer below it.
  // REPLACES the whole list so the loaded set stays one contiguous block.
  const loadMessagesFrom = useCallback(async (
    threadId: string,
    messageId: string,
    limit = 20,
  ): Promise<{ found: boolean } | null> => {
    try {
      const data = await versionControlApi.getVersionedChatHistory(
        projectId, threadId, limit, undefined, messageId,
      );
      if (!data || !Array.isArray(data.messages)) {
        throw new Error('Invalid API response structure');
      }

      const threadMessages = (data.messages || []).map(mapRawDevMessage);

      setMessages(threadMessages);
      onMessageUpdate?.(threadMessages);
      setHasMoreMessages(data.pagination?.has_more_older ?? false);
      applyNewerEdge(threadMessages, data.pagination?.has_more_newer ?? false);

      // cursor_fallback: the target no longer exists (stale nav index); it can
      // also resolve but be hidden from the active branch's visibility
      const found = !data.cursor_fallback && threadMessages.some((m: Message) => m.id === messageId);
      return { found };
    } catch (error) {
      console.error('❌ Error loading messages from target:', error);
      return null;
    }
  }, [projectId, onMessageUpdate, applyNewerEdge]);

  // Downward pagination below a historical window. Appends to the end; the
  // inclusive cursor re-fetches the boundary message, which the dedupe absorbs.
  const loadNewerMessages = useCallback(async (
    threadId: string,
    limit = 20,
  ): Promise<{ hasMoreNewer: boolean } | null> => {
    // Cursor must be a persisted DB id — optimistic/thinking entries at the
    // tail carry temp ids the backend cannot resolve
    const cursor = [...messagesRef.current].reverse().find(m => PERSISTED_ID_RE.test(m.id));
    if (!cursor) return null;
    try {
      const data = await versionControlApi.getVersionedChatHistory(
        projectId, threadId, limit, undefined, cursor.id,
      );
      if (!data || !Array.isArray(data.messages)) {
        throw new Error('Invalid API response structure');
      }

      const batch = (data.messages || []).map(mapRawDevMessage);
      const hasMoreNewer = data.pagination?.has_more_newer ?? false;

      if (batch.length > 0) {
        setMessages(prev => {
          const batchIds = new Set(batch.map((m: Message) => m.id));
          const next = [...prev.filter(m => !batchIds.has(m.id)), ...batch];
          queueMicrotask(() => onMessageUpdate?.(next));
          return next;
        });
      }
      applyNewerEdge(batch, hasMoreNewer);
      return { hasMoreNewer };
    } catch (error) {
      console.error('❌ Error loading newer messages:', error);
      return null;
    }
  }, [projectId, onMessageUpdate, applyNewerEdge]);

  // Check for incomplete conversations and set up waiting state/timer
  const checkIncompleteConversation = useCallback(async (threadId: string, skipMessageLoad = false) => {
    console.log(`🔍 Checking for incomplete conversation on thread: ${threadId}`);
    console.log(`📋 Current state - waitingState: ${waitingState}, isLoading: ${isLoading}, isStreaming: ${isStreaming}`);
    
    // Clear any existing timers
    if (waitingTimeoutRef.current) {
      console.log('🧹 Clearing existing timer before new check');
      window.clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
    if (messageTimeoutRef.current) {
      window.clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
    
    setIsCheckingState(true);
    
    try {
      // Check conversation state and thread status
      console.log(`🌐 Making API calls to check conversation state for thread: ${threadId}`);
      const { conversationState, threadStatus } = await checkConversationState(threadId);
      
      console.log('🔍 API Response - Conversation state:', conversationState);
      console.log('🔍 API Response - Thread status:', threadStatus);
      
      // Store the conversation state for UI usage
      setCurrentConversationState(conversationState);
      
      // Check for all active statuses for multi-window sync support
      // Active statuses: IN_PROGRESS, DEVELOPMENT_IN_PROGRESS
      const activeStatuses = ['IN_PROGRESS', 'DEVELOPMENT_IN_PROGRESS'];
      const isActivelyProcessing = conversationState && activeStatuses.includes(conversationState.execution_status);
      
      if (isActivelyProcessing) {
        console.log('⏳ ACTIVE CONVERSATION DETECTED! Process in progress, showing waiting state and starting 60s timer');
        console.log(`📊 Details: execution_status=${conversationState.execution_status}, is_active=${threadStatus?.is_active}`);
        
        if (!skipMessageLoad) {
          console.log('📥 Loading messages for incomplete conversation...');
          await loadMessages(threadId); // Load existing messages first
        } else {
          console.log('📥 SKIPPING message load for incomplete conversation (preserving local messages)');
        }
        
        setWaitingState('waiting_for_updates');
        
        // Set streaming state for in-progress threads (enables Stop button, disables textarea)
        setIsLoading(false);
        setIsStreaming(true);
        
        // Add waiting message to the conversation
        addWaitingMessage();
        
        // Start 60-second timeout for resume/discard popup [[memory:6385815]]
        console.log(`⏰ Starting 60-second timer for thread: ${threadId}`);
        console.log(`📌 Timer set with threadId: ${threadId}, currentThreadId: ${currentThreadId}, currentThreadIdRef: ${currentThreadIdRef.current}`);
        
        // Generate unique timeout ID to prevent race conditions
        const currentTimeoutId = ++timeoutIdRef.current;
        console.log(`🔢 Timeout ID: ${currentTimeoutId}`);
        
        // Capture the thread ID at timer creation to avoid stale closure issues
        const timerThreadId = threadId;
        waitingTimeoutRef.current = window.setTimeout(() => {
          console.log(`⏰ 60-second timeout reached for timer thread: ${timerThreadId}, timeout ID: ${currentTimeoutId}`);
          
          // Check if this timeout is still valid (not superseded by a newer one)
          if (timeoutIdRef.current !== currentTimeoutId) {
            console.log(`❌ Timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
            return;
          }
          
          // Get the FRESH current thread ID from the ref (not stale closure)
          const freshCurrentThreadId = currentThreadIdRef.current;
          console.log(`🎯 Fresh comparison - Timer thread: ${timerThreadId}, Current thread: ${freshCurrentThreadId}`);
          
          // Only show popup if user is still on the same thread
          if (freshCurrentThreadId === timerThreadId) {
            console.log('✅ User still on same thread, showing resume/discard popup');
            setWaitingState('show_resume_discard');
          } else {
            console.log('❌ User switched threads, not showing popup (was on thread:', timerThreadId, ', now on:', freshCurrentThreadId, ')');
          }
        }, 60000); // 60 seconds
        
        console.log('✅ Timer set successfully, returning true for incomplete conversation');
        return true; // Indicates incomplete conversation was found
      }
      
      // No incomplete conversation found
      console.log('✅ No incomplete conversation found - thread is complete or not active');
      console.log(`📊 Details: execution_status=${conversationState?.execution_status}, is_active=${threadStatus?.is_active}`);
      if (!skipMessageLoad) {
        await loadMessages(threadId);
      } else {
        console.log('📥 SKIPPING message load for completed conversation (preserving local messages)');
      }
      setWaitingState('none');
      return false;
      
    } catch (error) {
      console.error('❌ Error checking incomplete conversation (proceeding normally):', error);
      // Fall back to loading messages normally
      if (!skipMessageLoad) {
        await loadMessages(threadId);
      }
      setWaitingState('none');
      return false;
    } finally {
      setIsCheckingState(false);
    }
  }, [checkConversationState, loadMessages, addWaitingMessage]);

  // Handle reconnection flow
  const handleReconnection = useCallback(async (threadId: string) => {
    console.log(`🔄 Handling reconnection for thread: ${threadId}`);
    
    // Clear any existing timers and state for clean slate
    if (waitingTimeoutRef.current) {
      console.log('🧹 Clearing existing timeout before checking incomplete conversation');
      window.clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
    
    console.log(`🔍 About to check incomplete conversation for thread: ${threadId}`);
    
    setWaitingState('none');
    setIsLoading(false);
    setIsStreaming(false);
    
    await checkIncompleteConversation(threadId, false);
  }, [checkIncompleteConversation]);

  // Resume conversation (UI state management only - API call handled by dialog)
  const resumeConversation = useCallback(async (threadId: string): Promise<boolean> => {
    try {
      console.log(`🔄 Processing resume for thread: ${threadId} (API call handled by dialog)`);
      
      // Clear waiting state and start listening for updates via WebSocket
      console.log('📡 Resume initiated, setting state to waiting_for_updates (WebSocket-based)');
      setWaitingState('waiting_for_updates');
      setLastMessageTimestamp(Date.now());
      
      // Add agent thinking message for progress handling
      const thinkingMessage: Message = {
        id: 'agent-thinking',
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isThinking: true,
        thinkingMessage: 'Working on your request...'
      };
      
      setMessages(prev => {
        // Remove existing waiting and thinking messages if any
        const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
        return [...filtered, thinkingMessage];
      });
      
      // Set loading state to indicate we're expecting WebSocket updates
      console.log('🔄 Setting loading=true, streaming=false - expecting WebSocket updates');
      setIsLoading(true);
      setIsStreaming(false);
      
      // Reset the 60-second timer
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
      }
      
      // Generate unique timeout ID to prevent race conditions
      const currentTimeoutId = ++timeoutIdRef.current;
      console.log(`🔢 Resume timeout ID: ${currentTimeoutId}`);
      
      waitingTimeoutRef.current = window.setTimeout(() => {
        console.log(`⏰ Resume timeout reached, checking if still on same thread... (timeout ID: ${currentTimeoutId})`);
        
        // Check if this timeout is still valid (not superseded by a newer one)
        if (timeoutIdRef.current !== currentTimeoutId) {
          console.log(`❌ Resume timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
          return;
        }
        
        // Get fresh current thread ID from ref
        const freshCurrentThreadId = currentThreadIdRef.current;
        console.log(`🎯 Resume timeout - Timer thread: ${threadId}, Current thread: ${freshCurrentThreadId}`);
        
        // Only show popup if user is still on the same thread
        if (freshCurrentThreadId === threadId) {
          console.log('✅ User still on same thread, showing resume/discard popup');
          setWaitingState('show_resume_discard');
        } else {
          console.log('❌ User switched threads, not showing popup (was on thread:', threadId, ', now on:', freshCurrentThreadId, ')');
        }
      }, 60000);
      
      console.log('✅ Resume processing completed - waiting for WebSocket updates');
      return true;
    } catch (error) {
      console.error('Failed to process resume:', error);
      return false;
    }
  }, []);

  // Discard conversation (API call handled by dialog component)
  const discardConversation = useCallback(async (threadId: string): Promise<boolean> => {
    try {
      console.log(`🗑️ Processing discard for thread: ${threadId} (API call handled externally)`);
      
      // Remove waiting message and clear waiting state and timers
      removeWaitingMessage();
      setWaitingState('none');
      setIsLoading(false);
      setIsStreaming(false);
      
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      if (messageTimeoutRef.current) {
        window.clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = null;
      }
      
      // Reload conversation to get final state after discard
      await loadMessages(threadId, true); // Force reload after discard
      
      console.log('✅ Conversation discard processed successfully and reloaded');
      return true;
    } catch (error) {
      console.error('Failed to process discard conversation:', error);
      return false;
    }
  }, [removeWaitingMessage, loadMessages]);

  // Resume the 60-second timeout (used after credential capture completes)
  const resumeWaitingTimeout = useCallback(() => {
    if (!currentThreadId) return;
    
    console.log('▶️ Resuming 60-second timer after credential capture');
    
    // Clear any existing timeout
    if (waitingTimeoutRef.current) {
      window.clearTimeout(waitingTimeoutRef.current);
    }
    
    // Generate unique timeout ID to prevent race conditions
    const currentTimeoutId = ++timeoutIdRef.current;
    console.log(`🔢 Resumed timeout ID: ${currentTimeoutId}`);
    
    // Capture the thread ID at timer creation
    const timerThreadId = currentThreadId;
    waitingTimeoutRef.current = window.setTimeout(() => {
      console.log(`⏰ Resumed timeout reached for thread: ${timerThreadId}, timeout ID: ${currentTimeoutId}`);
      
      // Check if this timeout is still valid
      if (timeoutIdRef.current !== currentTimeoutId) {
        console.log(`❌ Resumed timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
        return;
      }
      
      // Get fresh current thread ID
      const freshCurrentThreadId = currentThreadIdRef.current;
      console.log(`🎯 Fresh comparison after resume - Timer thread: ${timerThreadId}, Current thread: ${freshCurrentThreadId}`);
      
      if (freshCurrentThreadId === timerThreadId) {
        console.log('✅ User still on same thread after resume, showing resume/discard popup');
        setWaitingState('show_resume_discard');
      } else {
        console.log('❌ User switched threads after resume, not showing popup (was on thread:', timerThreadId, ', now on:', freshCurrentThreadId, ')');
      }
    }, 60000);
  }, [currentThreadId]);

  // Wait for more time (extend timeout)
  const waitMore = useCallback(() => {
    console.log('⏰ Extending wait time for 60 more seconds');
    
    setWaitingState('waiting_for_updates');
    
    // Capture current thread ID for timeout check
    const waitingThreadId = currentThreadId;
    
    // Reset the 60-second timer
    if (waitingTimeoutRef.current) {
      window.clearTimeout(waitingTimeoutRef.current);
    }
    
    // Generate unique timeout ID to prevent race conditions
    const currentTimeoutId = ++timeoutIdRef.current;
    console.log(`🔢 Wait more timeout ID: ${currentTimeoutId}`);
    
    waitingTimeoutRef.current = window.setTimeout(() => {
      console.log(`⏰ Wait more timeout reached, checking if still on same thread... (timeout ID: ${currentTimeoutId})`);
      
      // Check if this timeout is still valid (not superseded by a newer one)
      if (timeoutIdRef.current !== currentTimeoutId) {
        console.log(`❌ Wait more timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
        return;
      }
      
      // Get fresh current thread ID from ref
      const freshCurrentThreadId = currentThreadIdRef.current;
      console.log(`🎯 Wait more timeout - Timer thread: ${waitingThreadId}, Current thread: ${freshCurrentThreadId}`);
      
      // Only show popup if user is still on the same thread
      if (freshCurrentThreadId === waitingThreadId) {
        console.log('✅ User still on same thread, showing resume/discard popup');
        setWaitingState('show_resume_discard');
      } else {
        console.log('❌ User switched threads, not showing popup (was on thread:', waitingThreadId, ', now on:', freshCurrentThreadId, ')');
      }
    }, 60000);
  }, [currentThreadId]);

  // Credential submission function
  const submitCapturedCredentials = useCallback(async (requestId: string, values: Record<string, string>) => {
    try {
      // Check if user skipped providing credentials
      const userSkipped = values['__skipped_by_user'] === 'true';
      
      const response = await api.post(`/dev-agent/projects/${projectId}/credentials/submit`, {
        request_id: requestId,
        captured_values: values
      });
      
      // Show appropriate message based on whether user skipped or provided credentials
      if (userSkipped) {
        addProcessingMessage('⏭️ Credentials skipped - proceeding without them');
      } else {
        addProcessingMessage('✅ Credentials submitted successfully');
      }
      
      // Clear the credential capture request after successful submission
      setCredentialCaptureRequest(null);
      
      // Resume the 60-second timeout since user interaction is complete
      resumeWaitingTimeout();
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to submit credentials:', error);
      
      // Check if this was a skip attempt or regular submission for error message
      const userSkipped = values['__skipped_by_user'] === 'true';
      if (userSkipped) {
        addProcessingMessage('Failed to skip credentials');
      } else {
        addProcessingMessage('Failed to submit credentials');
      }
      
      // Even on error, resume the timeout since user tried to submit
      resumeWaitingTimeout();
      
      throw error;
    }
  }, [projectId, resumeWaitingTimeout]);

  // Check for pending credential requests on thread selection (for state persistence)
  const checkForPendingCredentialRequest = useCallback(async (threadId: string) => {
    try {
      const response = await api.get(`/dev-agent/projects/${projectId}/threads/${threadId}/pending-credential-request`);
      if (response.data.has_pending_request) {
        const requestData = response.data.request_data;
        setCredentialCaptureRequest({
          requestId: requestData.request_id,
          parameters: requestData.parameters,
          envFilePath: requestData.env_file_path
        });
        console.log('🔄 Restored pending credential request:', requestData.request_id);
      }
    } catch (error) {
      console.error('❌ Failed to check for pending credential request:', error);
    }
  }, [projectId]);

  // Select thread (handles reconnection logic)
  const selectThread = useCallback(async (threadId: string | undefined) => {
    console.log(`🎯 selectThread called with: ${threadId} (current: ${currentThreadId})`);
    console.log('🚀 [DEV_MSG_FLOW] selectThread - current messages before:', messages.length);

    // Disconnect from previous thread
    if (currentThreadId && currentThreadId !== threadId) {
      console.log(`🔌 Disconnecting from previous thread: ${currentThreadId}`);
      webSocket.disconnect();
    }

    console.log(`📝 Setting currentThreadId to: ${threadId}`);
    setCurrentThreadId(threadId);
    
    // Immediately update the ref to avoid timing issues with async state updates
    currentThreadIdRef.current = threadId;
    
    if (!threadId) {
      setMessages([]);
      setWaitingState('none');
      setIsLoading(false);
      setIsStreaming(false);

      // Clear any active timeouts
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      if (messageTimeoutRef.current) {
        window.clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = null;
      }

      return;
    }

    // Handle reconnection flow (now robust for both new and existing threads)
    await handleReconnection(threadId);

    // Connect to WebSocket
    try {
      await webSocket.connectToThread(threadId, {
        onReconnect: async () => {
          // Check for incomplete conversations on reconnection (skip message reload since we already have them)
          await checkIncompleteConversation(threadId, true);
        },
        onMessage: (message) => {
          // Pass the raw message to the optional callback if provided
          if (onRawWebSocketMessage) {
            onRawWebSocketMessage(message);
          }

          // Handle dev_phase_update for multi-window sync
          if (message.type === 'dev_phase_update') {
            // Update processing message to show current phase progress
            setMessages(prev => {
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking' && msg.id !== 'processing');
              const progressMessage: Message = {
                id: 'agent-thinking',
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                isThinking: true,
                thinkingMessage: message.data.message || 'Processing...'
              };
              return [...filtered, progressMessage];
            });
          }

          // Update last message timestamp when receiving updates
          setLastMessageTimestamp(Date.now());

          // If we were waiting for updates and received a message, clear waiting state
          setWaitingState(currentWaitingState => {
            if (currentWaitingState === 'waiting_for_updates') {
              return 'none';
            }
            return currentWaitingState;
          });

          // Clear the 60-second timeout for any message indicating active processing
          // This prevents false resume/discard popups when backend is actively working
          // Support both old and new event names for backwards compatibility
          const activeProcessingMessageTypes = [
            'dev_text_start', 'dev_tool_start', 'dev_tool_progress', 'dev_tool_result',
            'dev_file_change', 'dev_message_start', 'dev_message_streaming', 'dev_message_update',
            'dev_status', 'dev_tool_execution', 'dev_agent_log', 'dev_message_complete', 'dev_token_usage_complete',
            'dev_phase_update', 'dev_processing_status',
            'dev_checkpoint_created', 'dev_checkpoint_reverted'
          ];

          if (activeProcessingMessageTypes.includes(message.type)) {
            if (waitingTimeoutRef.current) {
              window.clearTimeout(waitingTimeoutRef.current);
              waitingTimeoutRef.current = null;
            }
          }

          // Handle dev_processing_status for multi-window sync (initial state on connect)
          if (message.type === 'dev_processing_status') {
            console.log('📡 [DEV] Processing status on connect:', message.data);
            
            if (message.data.is_processing) {
              setIsLoading(true);
              setIsStreaming(true);
              
              // Show thinking indicator if not already present
              setMessages(prev => {
                const hasThinking = prev.some(m => m.isThinking);
                if (hasThinking) return prev;
                
                const thinkingMessage: Message = {
                  id: 'agent-thinking',
                  role: 'assistant',
                  content: '',
                  timestamp: new Date(),
                  isThinking: true,
                  thinkingMessage: message.data.message || 'Processing...'
                };
                return [...prev, thinkingMessage];
              });
            }
            return; // Early return for processing status
          }

          // Handle different message types - dev_message for Dev Mode multi-window sync
          if (message.type === 'dev_message') {
            setMessages(prev => {
              if (message.data.role === 'user') {
                const lastUserMessageIndex = [...prev].reverse().findIndex(m => m.role === 'user');
                
                if (lastUserMessageIndex !== -1) {
                  // Calculate actual index since we used reverse()
                  const actualIndex = prev.length - 1 - lastUserMessageIndex;
                  const lastUserMessage = prev[actualIndex];
                  
                  // If the last user message has an optimistic ID (e.g. user-12345) and same content,
                  // we should REPLACE it with the canonical backend message.
                  if (lastUserMessage.id.startsWith('user-') && lastUserMessage.content === message.data.content) {
                    // The backend's dev_message event for user messages only carries text
                    // fields (id, role, content, timestamp, user_info). Attachment data
                    // (file_ids, image_ids, resolved Attachment objects) is hydrated only
                    // during REST API message loads, NOT in the real-time WS event.
                    // Preserve attachment fields from the optimistic message so attachments
                    // stay visible without requiring a page reload.
                    const mappedMessage = {
                      ...message.data,
                      // Prefer server-supplied values; fall back to what the user saw locally.
                      attachments: message.data.attachments?.length
                        ? message.data.attachments
                        : (lastUserMessage.attachments || []),
                      images: message.data.images?.length
                        ? message.data.images
                        : (lastUserMessage.images || []),
                      fileIds: message.data.fileIds?.length
                        ? message.data.fileIds
                        : (lastUserMessage.fileIds || []),
                      artifactIds: message.data.artifactIds?.length
                        ? message.data.artifactIds
                        : (lastUserMessage.artifactIds || []),
                      imageIds: message.data.imageIds?.length
                        ? message.data.imageIds
                        : (lastUserMessage.imageIds || []),
                      fileNames: message.data.fileNames?.length
                        ? message.data.fileNames
                        : (lastUserMessage.fileNames || []),
                      artifactNames: message.data.artifactNames?.length
                        ? message.data.artifactNames
                        : (lastUserMessage.artifactNames || []),
                      messageType: message.data.messageType
                    };
                    
                    const updated = [...prev];
                    updated[actualIndex] = mappedMessage as Message;
                    return updated;
                  }
                  
                  // If the exact ID already exists (e.g. from a refresh), just skip
                  if (prev.some(m => m.id === message.data.id)) {
                    return prev;
                  }
                }
              }

              // Use camelCase fields directly from backend.
              // The backend sends attachment IDs in snake_case (file_ids, image_ids,
              // artifact_ids); map them to camelCase so ChatMessage can at least
              // count them. Full hydration (names/URLs) only happens on REST API load.
              const mappedMessage = {
                ...message.data,
                fileIds: message.data.fileIds || message.data.file_ids || [],
                artifactIds: message.data.artifactIds || message.data.artifact_ids || [],
                imageIds: message.data.imageIds || message.data.image_ids || [],
                attachments: message.data.attachments || [],
                messageType: message.data.messageType
              };

              // Remove thinking message and add new message
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
              const updated = [...filtered, mappedMessage];

              onMessageUpdate?.(updated);
              return updated;
            });
            // Only an ASSISTANT message means the turn produced output and the
            // input can go back to its idle (Send) state. The echo of the user's
            // own message is not a completion signal — the backend broadcasts it
            // up front, before ticket @-reference retrieval, which can run for a
            // long time (longest when no ticket is tagged and the agent has to
            // find the relevant ones itself). Clearing these flags on a `user`
            // event hid the Stop button for that whole window — exactly when the
            // user wants to stop — until the first phase/tool event set them
            // back to true.
            if (message.data.role !== 'user') {
              setIsLoading(false);
              setIsStreaming(false);
            }
          } else if (message.type === 'dev_message_update' || message.type === 'dev_message_updated') {
            setMessages(prev => {
              const updated = prev.map(msg => {
                if (msg.id === message.data.id) {
                  // Update existing message with new content and metadata
                  return {
                    ...msg,
                    content: stripCommitTag(message.data.content || ''),
                    timestamp: message.data.timestamp ? new Date(message.data.timestamp) : msg.timestamp
                  };
                }
                return msg;
              });
              onMessageUpdate?.(updated);
              return updated;
            });
          } else if (message.type === 'queue_job_started') {
            console.log('🚀 [DEV_MSG_FLOW] queue_job_started');
            // Do NOT optimistically add the queued user message here. The backend
            // broadcasts the authoritative `dev_message` (role: user) the instant the
            // job starts, and that is the single source that renders it. Adding it here
            // too produced a duplicate user message (optimistic + canonical) that only
            // reconciled when content + ordering happened to line up — and vanished on
            // refresh. Just clear any placeholder and reflect the running state.
            setMessages(prev => prev.filter(msg => msg.id !== 'processing'));
            setIsLoading(true);
            setIsStreaming(true);
          } else if (message.type === 'queue_job_completed') {
            // The stopping run's queue job is done, so the run has ended, even if its
            // own completion event was missed.
            endStopping();
          } else if (message.type === 'dev_phase_update') {
            // Handle workflow phase updates (development)
            const phaseMessage = message.data?.message || 'Processing your request...';
            const phaseStatus = message.data?.status;
            console.log(`🔄 DEV_PHASE_UPDATE: phase=${message.data?.phase}, status=${phaseStatus}, message="${phaseMessage}"`);

            // Handle error status immediately
            if (phaseStatus === 'error') {
              console.error('❌ Phase error received:', phaseMessage);

              // Clear loading/streaming states
              setIsLoading(false);
              setIsStreaming(false);

              // Remove thinking message and show error message
              setMessages(prev => {
                // Remove thinking message
                const filtered = prev.filter(msg => msg.id !== 'agent-thinking' && msg.id !== 'processing' && !msg.isThinking);

                // Add error message
                const errorMessage: Message = {
                  id: generateMessageId('phase-error'),
                  role: 'assistant',
                  content: phaseMessage,
                  timestamp: new Date(),
                  isThinking: false
                };

                const updated = [...filtered, errorMessage];
                onMessageUpdate?.(updated);
                return updated;
              });

              return; // Early return to prevent further processing
            }

            console.log('🚀 [DEV_MSG_FLOW] dev_phase_update - about to update thinking message');
            updateThinkingMessage(phaseMessage);
            console.log('🚀 [DEV_MSG_FLOW] dev_phase_update - thinking message updated');
            setIsLoading(true);
            
            // Handle phase-specific UI updates for multi-window sync
            if (message.data?.phase === 'development') {
              // Development phase started - show development progress (enables Stop button)
              console.log('🚀 Development phase started');
              setIsStreaming(true);
            }
          } else if (message.type === 'dev_agent_thinking' || message.type === 'dev_agent_start') {
            // Show backend message or fallback to generic message for basic agent states
            const agentMessage = (message.data && message.data.message) ? message.data.message : 'Working on your request...';
            console.log(`🤖 Updating thinking message: "${agentMessage}"`);
            updateThinkingMessage(agentMessage);
            setIsLoading(true);
          } else if (message.type === 'dev_tool_start') {
            // Handle tool start - add to thinking message's tool executions
            console.log(`🔧 DEV_TOOL_START: ${message.data.id}`, message.data);
            
            const friendlyMessage = getFriendlyEventMessage(message);
            console.log(`🔥🔥🔥 TOOL_START UPDATE: "${friendlyMessage}"`);
            
            // Update thinking message with tool execution
            setMessages(prev => {
              const thinkingMsg = prev.find(msg => msg.id === 'agent-thinking');
              
              if (thinkingMsg) {
                // Add new tool execution to the thinking message
                const newToolExecution = {
                  id: message.data.id,
                  status: 'running',
                  timestamp_start: message.data.timestamp_start,
                  friendly_message: message.data.friendly_message,
                };

                // Upsert by tool id: a duplicate tool_start (e.g. delivered over more
                // than one socket after a reconnect) must not create a second entry,
                // otherwise React sees two children with the same key.
                const existing = thinkingMsg.toolExecutions || [];
                const alreadyPresent = existing.some(tool => tool.id === message.data.id);
                const updatedToolExecutions = alreadyPresent
                  ? existing.map(tool => (tool.id === message.data.id ? { ...tool, ...newToolExecution } : tool))
                  : [...existing, newToolExecution];
                
                return prev.map(msg => 
                  msg.id === 'agent-thinking'
                    ? { ...msg, toolExecutions: updatedToolExecutions, thinkingMessage: friendlyMessage }
                    : msg
                );
              } else {
                // Create thinking message with tool execution
                const thinkingMessage: Message = {
                  id: 'agent-thinking',
                  role: 'assistant',
                  content: '',
                  timestamp: new Date(),
                  isThinking: true,
                  thinkingMessage: friendlyMessage,
                  toolExecutions: [{
                    id: message.data.id,
                    status: 'running',
                    timestamp_start: message.data.timestamp_start,
                    friendly_message: message.data.friendly_message,
                  }]
                };
                return [...prev, thinkingMessage];
              }
            });
            
            setIsLoading(true);
          } else if (message.type === 'dev_tool_result') {
            // Handle tool completion - update the tool execution status
            console.log(`✅ DEV_TOOL_RESULT: ${message.data.id}`, message.data);
            
            const friendlyMessage = getFriendlyEventMessage(message);
            console.log(`🔥🔥🔥 TOOL_RESULT UPDATE: "${friendlyMessage}"`);
            
            // Update the tool execution in thinking message
            setMessages(prev => {
              const thinkingMsg = prev.find(msg => msg.id === 'agent-thinking');
              
              if (thinkingMsg && thinkingMsg.toolExecutions) {
                const updatedToolExecutions = thinkingMsg.toolExecutions.map(tool =>
                  tool.id === message.data.id
                    ? {
                        ...tool,
                        status: message.data.status || 'completed',
                        friendly_message: message.data.friendly_message
                      }
                    : tool
                );
                
                return prev.map(msg =>
                  msg.id === 'agent-thinking'
                    ? { ...msg, toolExecutions: updatedToolExecutions, thinkingMessage: friendlyMessage }
                    : msg
                );
              }
              
              return prev;
            });
            
            setIsLoading(true);
          } else if (message.type === 'dev_status' || message.type === 'dev_tool_progress') {
            // Display backend StreamingEvent messages as Veylo Agent updates
            const friendlyMessage = getFriendlyEventMessage(message);
            console.log(`🔥🔥🔥 PROGRESS UPDATE: ${message.type} -> "${friendlyMessage}"`);
            
            // Update with backend message (updateThinkingMessage will ignore blanks)
            updateThinkingMessage(friendlyMessage);
            setIsLoading(true);
          } else if (message.type === 'dev_message_start') {
            // Just create assistant message, keep thinking message for ongoing tool events  
            console.log('🔥🔥🔥 MESSAGE_START RECEIVED');
            setMessages(prev => {
              const assistantMessage = {
                id: generateMessageId('assistant', message.data?.message_id),
                role: 'assistant' as const,
                content: '',
                timestamp: new Date(),
                isThinking: false
              };
              const updated = [...prev, assistantMessage];
              console.log('🔥🔥🔥 Created assistant message placeholder');
              onMessageUpdate?.(updated);
              return updated;
            });
            setIsLoading(false);
            setIsStreaming(true);
          } else if (message.type === 'dev_text_start') {
            // Text streaming is starting
            setIsStreaming(true);
          // Removed iteration_response handling - not needed
          // Tool progress is already shown via dev_tool_start/progress/result events
          } else if (message.type === 'dev_agent_log' || message.type === 'dev_tool_execution') {
            // Ignore technical details - keep showing simple friendly message
            // (No action needed, just keep the existing friendly "Working on your request..." message)
          } else if (message.type === 'dev_message_streaming') {
            // Handle legacy streaming updates
            setIsStreaming(true);
            setMessages(prev => {
              const updated = [...prev];
              // Find the last assistant message and update it
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant' && updated[i].id !== 'agent-thinking') {
                  updated[i] = { ...updated[i], content: stripCommitTag(message.data.content || '') };
                  break;
                }
              }
              onMessageUpdate?.(updated);
              return updated;
            });
          } else if (message.type === 'dev_message_complete') {
            // Complete response received - create final assistant message
            console.log('🔥🔥🔥 DEV_MESSAGE_COMPLETE RECEIVED!');
            console.log('🔥🔥🔥 Raw message data:', JSON.stringify(message.data, null, 2));
            
            const finalContent = stripCommitTag(message.data?.content || message.data?.message || '');
            console.log('🔥🔥🔥 Extracted final content:', finalContent);
            
            if (!finalContent || finalContent.trim() === '') {
              console.error('❌ No valid content in message_complete event!');
              console.error('❌ Message data:', message.data);
              // Use fallback content
              const fallbackContent = 'Response completed but no content received.';
              console.log('🔧 Using fallback content:', fallbackContent);
            }

            // Note: URL-based preview panel trigger moved to dev_task_complete
            // to only open after the entire development cycle finishes
            
            // Get tool_executions from the event data (sent by backend)
            const eventToolExecutions = message.data?.tool_executions || [];
            console.log('🔥🔥🔥 MESSAGE_COMPLETE: tool_executions from event:', eventToolExecutions.length);
            console.log('🔥🔥🔥 MESSAGE_COMPLETE: tool_executions details:', JSON.stringify(eventToolExecutions, null, 2));
            
            setMessages(prev => {
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: Processing final response, current messages:', prev.length);
              const userMessages = prev.filter(m => m.role === 'user');
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: User messages before update:', userMessages.length);
              
              // Get tool executions from thinking message before removing it (fallback)
              const thinkingMsg = prev.find(msg => msg.id === 'agent-thinking');
              const thinkingToolExecutions = thinkingMsg?.toolExecutions || [];
              // Prefer tool_executions from event data, fallback to thinking message
              const toolExecutions = eventToolExecutions.length > 0 ? eventToolExecutions : thinkingToolExecutions;
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: Transferring', toolExecutions.length, 'tool executions to final message (from event:', eventToolExecutions.length, ', from thinking:', thinkingToolExecutions.length, ')');
              
              // Remove all thinking messages 
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
              
              // Find the existing assistant message created by dev_message_start and UPDATE it
              const existingAssistantMsg = [...filtered].reverse().find(msg =>
                msg.role === 'assistant' && msg.id.includes('assistant')
              );
              let updated;
              if (existingAssistantMsg) {
                updated = filtered.map(msg =>
                  msg.id === existingAssistantMsg.id
                    ? {
                        ...msg,
                        content: finalContent || 'Response completed but no content received.',
                        toolExecutions: toolExecutions.length > 0 ? toolExecutions : undefined,
                        isThinking: false
                      }
                    : msg
                );
              } else {
                const assistantMessage = {
                  id: generateMessageId('assistant-complete', message.data?.message_id),
                  role: 'assistant' as const,
                  content: finalContent || 'Response completed but no content received.',
                  timestamp: new Date(),
                  isThinking: false,
                  toolExecutions: toolExecutions.length > 0 ? toolExecutions : undefined
                };
                updated = [...filtered, assistantMessage];
              };
              
              const userMessagesAfter = updated.filter(m => m.role === 'user');
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: User messages after update:', userMessagesAfter.length);
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: Added final response, total messages:', updated.length);
              
              onMessageUpdate?.(updated);
              return updated;
            });
            
            // Only stop loading, but keep streaming true until task_complete
            // This ensures the stop button remains visible during development
            setIsLoading(false);
            console.log('🔥🔥🔥 Loading stopped, but keeping streaming for stop button');
          } else if (message.type === 'dev_token_usage_complete') {
            // Handle token usage completion - add token usage to the last assistant message
            console.log('💰 DEV Token usage received:', message.data);
            
            setMessages(prev => {
              const updated = [...prev];
              
              // Find the last assistant message and add token usage
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant' && updated[i].id !== 'agent-thinking') {
                  updated[i] = { 
                    ...updated[i], 
                    tokenUsage: {
                      inputTokens: message.data.input_tokens || 0,
                      outputTokens: message.data.output_tokens || 0,
                      cacheCreationInputTokens: message.data.cache_creation_input_tokens || 0,
                      cacheReadInputTokens: message.data.cache_read_input_tokens || 0,
                      serviceTier: message.data.service_tier || 'standard',
                      costs: message.data.costs
                    }
                  };
                  break;
                }
              }
              
              onMessageUpdate?.(updated);
              return updated;
            });
          } else if (message.type === 'dev_task_complete' || message.type === 'dev_workflow_complete') {
            // Handle final task completion event
            console.log('✅ Dev Task/Workflow completed:', message.type, message.data);

            const messageId = message.data?.message_id;
            const developmentTiming = message.data?.development_timing;
            const modelPref = message.data?.model_preference;

            setMessages(prev => {
              // Safety net: always remove thinking messages on task completion
              let updated = prev.filter(msg => msg.id !== 'agent-thinking' && !(msg.isThinking && !msg.content));

              // Find the last assistant message and update it with all data at once
              let foundAssistant = false;
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant') {
                  const updates: any = { isThinking: false };
                  if (messageId) {
                    console.log(`📨 Updating message ID from ${updated[i].id} to ${messageId}`);
                    updates.id = messageId;
                  }
                  if (developmentTiming) {
                    console.log('⏱️ Development timing added to message:', messageId || updated[i].id);
                    updates.developmentTiming = developmentTiming;
                  }
                  if (modelPref) {
                    updates.model_preference = modelPref;
                  }
                  updated[i] = { ...updated[i], ...updates };
                  foundAssistant = true;
                  break;
                }
              }

              // If dev_message_complete was missed (e.g. WebSocket reconnect), create a
              // placeholder so the user isn't stuck on "thinking...". The real content
              // will appear on page refresh from the DB.
              if (!foundAssistant && messageId) {
                console.log('⚠️ No assistant message found at task_complete — creating placeholder');
                updated.push({
                  id: messageId,
                  role: 'assistant' as const,
                  content: 'Response completed. Refresh to see full content.',
                  timestamp: new Date(),
                  isThinking: false,
                  developmentTiming,
                  model_preference: modelPref,
                });
              }

              onMessageUpdate?.(updated);
              return updated;
            });

            // Handle specific workflow statuses for enhanced workflow
            // Note: workflow_complete with 'development_started' status comes AFTER task_complete
            // so we should NOT re-enable the stop button at this point
            if (message.type === 'dev_workflow_complete' && message.data?.status) {
              console.log('🎯 [ENHANCED WORKFLOW] Dev workflow status:', message.data.status);

              if (message.data.status === 'development_started') {
                // This status indicates development WAS started, but workflow_complete means it's now done
                // Don't re-enable stop button here - development is already complete
                console.log('🚀 [ENHANCED WORKFLOW] Direct development completed via WebSocket');

              } else if (message.data.status === 'credit_error') {
                console.log('💳 [ENHANCED WORKFLOW] Credit error via WebSocket');
                // Clear ALL loading/active states to re-enable input
                setIsLoading(false);
                setIsStreaming(false);

                // Add credit error message to the chat
                const creditErrorMessage = {
                  id: generateMessageId('credit-error', message.data?.message_id),
                  role: 'assistant' as const,
                  content: message.data.message || '💳 Insufficient Credits',
                  timestamp: new Date(),
                  isThinking: false,
                  isCreditError: true
                };

                setMessages(prev => {
                  // Remove any thinking messages and add credit error
                  const filtered = prev.filter(msg => !msg.isThinking);
                  const updated = [...filtered, creditErrorMessage];
                  onMessageUpdate?.(updated);
                  return updated;
                });

              } else {
                console.log('ℹ️ [ENHANCED WORKFLOW] Other workflow status:', message.data.status);
              }
            }

            // Only process stats/state updates for dev_task_complete, not dev_workflow_complete
            // This prevents duplicate processing and flicker
            if (message.type === 'dev_task_complete') {
              // A run being stopped from another worker has now really ended.
              endStopping();

              // Fetch updated project stats after development completion
              // Note: project_id is at top level of message for dev_task_complete, not inside data
              const taskProjectId = message.project_id || projectId;

              // Use async function to handle API calls
              const fetchUpdatedStats = async () => {
                if (onProjectStatsUpdate && taskProjectId) {
                  try {
                    // Fetch updated stats from API
                    const response = await api.get(`/projects/${taskProjectId}/stats`);
                    // Create stats object with just the cost data
                    const statsUpdate = { total_cost: parseFloat(response.data.total_cost) || 0 };
                    onProjectStatsUpdate(taskProjectId, statsUpdate);
                  } catch (err) {
                    console.error('Failed to fetch updated project stats:', err);
                  }
                }
              };

              // Call the async function
              fetchUpdatedStats();

              // Ensure loading/streaming states are stopped when development is truly complete
              setIsLoading(false);
              setIsStreaming(false);
              console.log('🛑 Development completed - stop button now hidden');

              // Check if the last assistant message contains URLs to trigger preview panel
              setMessages(prev => {
                const lastAssistant = [...prev].reverse().find(m => m.role === 'assistant' && m.content);
                if (lastAssistant?.content) {
                  const urlPattern = /https?:\/\/[^\s]+/g;
                  const match = lastAssistant.content.match(urlPattern);
                  if (match) {
                    console.log('🌐 [useDevAgent] dev_task_complete: URLs found in final message — triggering preview');
                    onNewMessageWithUrls?.(match[0]);
                  }
                }
                return prev; // Don't modify messages, just read
              });
            }
          } else if (message.type === 'dev_credential_capture_request') {
            console.log('🔑 CREDENTIAL_CAPTURE_REQUEST RECEIVED:', message.data);
            
            // Pause the 60-second timeout while waiting for credentials
            if (waitingTimeoutRef.current) {
              console.log('⏸️ Pausing 60-second timer during credential capture');
              window.clearTimeout(waitingTimeoutRef.current);
              waitingTimeoutRef.current = null;
            }
            
            setCredentialCaptureRequest({
              requestId: message.data.request_id,
              parameters: message.data.parameters,
              envFilePath: message.data.env_file_path
            });
            // Keep isLoading true to continue showing "Thinking..." message with tool updates
            // Keep isStreaming true to maintain Stop button and continue receiving WebSocket updates
            console.log('🔑 Credential modal shown - keeping loading/streaming state to show tool updates');
          } else if (message.type === 'dev_credentials_written_to_env') {
            console.log('🔑 CREDENTIALS_WRITTEN_TO_ENV RECEIVED:', message.data);
            setCredentialCaptureRequest(null);
            addProcessingMessage(`✅ Credentials written to ${message.data.file_path} (${message.data.filled_count}/${message.data.total_count} filled)`);
            
            // Resume the 60-second timeout since credential process is complete
            resumeWaitingTimeout();
          } else if (message.type === 'dev_project_stats_update') {
            // Handle real-time project statistics updates
            console.log('📊 PROJECT_STATS_UPDATE RECEIVED:', message.data);
            console.log('📊 Project ID from event:', message.data?.project_id);
            console.log('📊 Token stats from event:', message.data?.token_stats);
            console.log('📊 onProjectStatsUpdate callback available:', !!onProjectStatsUpdate);

            if (onProjectStatsUpdate && message.data?.project_id && message.data?.token_stats) {
              console.log('📊 ✅ Calling onProjectStatsUpdate callback with data:', {
                projectId: message.data.project_id,
                tokenStats: message.data.token_stats
              });
              onProjectStatsUpdate(message.data.project_id, message.data.token_stats);
            } else {
              console.warn('📊 ❌ Cannot call onProjectStatsUpdate callback:', {
                hasCallback: !!onProjectStatsUpdate,
                hasProjectId: !!message.data?.project_id,
                hasTokenStats: !!message.data?.token_stats
              });
            }
          } else if (message.type === 'dev_error') {
            // Handle error messages from backend (Claude API errors, etc.)
            console.error('❌ Dev Error received via WebSocket:', message.data);

            // A run being stopped from another worker ended in an error instead.
            endStopping();

            // Clear loading/streaming states
            setIsLoading(false);
            setIsStreaming(false);

            // Extract error message with robust fallbacks
            // Check for error_user_message first (structured error from backend)
            let errorMessage = 'An error occurred. Please try again.';
            if (message.data && message.data.error_user_message && message.data.error_user_message.message) {
              errorMessage = message.data.error_user_message.message;
            } else if (message.data && message.data.message) {
              errorMessage = message.data.message;
            } else if (message.data && typeof message.data === 'string') {
              errorMessage = message.data;
            }

            // Create error message for display as regular assistant message
            const assistantErrorMessage: Message = {
              id: generateMessageId('error', message.data?.message_id),
              role: 'assistant',
              content: errorMessage,
              timestamp: new Date(),
              isThinking: false
            };

            setMessages(prev => {
              // Remove any thinking messages and add error message
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking' && !msg.isThinking);
              const updated = [...filtered, assistantErrorMessage];
              onMessageUpdate?.(updated);
              return updated;
            });
          } else if (message.type === 'dev_checkpoint_created') {
            console.log('✅ Checkpoint created:', message.data);
            const checkpointData = message.data;
            if (checkpointData) {
              setMessages(prev => {
                const updated = [...prev];
                // Find the last assistant message — at this point the DB message_id
                // may not have been applied yet (dev_task_complete hasn't fired),
                // so match the same way dev_token_usage_complete does.
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i].role === 'assistant' && updated[i].id !== 'agent-thinking') {
                    updated[i] = {
                      ...updated[i],
                      gitCommitHash: checkpointData.commit_hash,
                      checkpointTitle: checkpointData.checkpoint_title,
                    };
                    break;
                  }
                }
                onMessageUpdate?.(updated);
                return updated;
              });
            }

          } else if (message.type === 'dev_checkpoint_reverted') {
            console.log('↩️ Checkpoint reverted:', message.data);
            // Status and message reload are handled by handleInlineCheckpointRevert in ChatPage.
            // This handler covers multi-window sync (passive windows).
            clearViewingSegment();
            if (currentThreadId) {
              loadMessages(currentThreadId, true);
            }

          } else if (message.type === 'dev_live_test_plan') {
            // Full test plan received at the start of functional_test
            console.log('📋 Test plan received:', message.data.test_plan);
            setLiveTestState(prev => ({
              ...prev,
              testPlan: message.data.test_plan || [],
            }));

          } else if (message.type === 'dev_live_batch_initializing') {
            // New batch is initializing - show loading state in TestRecordingViewer
            console.log('🎬 Batch initializing:', message.data);
            setLiveTestState(prev => ({
              ...prev,
              isLive: false,
              isBatchInitializing: true,
              projectId: message.data.project_id,
              messageId: message.data.message_id,
              liveFrame: null,
            }));

          } else if (message.type === 'dev_live_test_started') {
            // Live test recording has started - clear initializing state
            console.log('🎬 Live test started:', message.data);
            const batchIndex = message.data.batch_index ?? 0;
            // Parse scenarios from the message if available (for legacy/current batch)
            const scenariosList: ScenarioItem[] = (message.data.scenarios || []).map((name: string, index: number) => ({
              index,
              name,
              status: index === 0 ? 'running' as const : 'pending' as const,
            }));
            
            setLiveTestState(prev => {
              // Update test plan to mark first scenario of this batch as running
              const updatedTestPlan = prev.testPlan.map((batch, bIdx) => {
                if (bIdx === batchIndex) {
                  return {
                    ...batch,
                    scenarios: batch.scenarios.map((s, sIdx) => ({
                      ...s,
                      status: sIdx === 0 ? 'running' as const : s.status,
                    })),
                  };
                }
                return batch;
              });
              
              return {
                ...prev,
                isLive: true,
                isBatchInitializing: false,
                recordingPath: message.data.recording_path,
                currentAction: 'Starting test...',
                currentScenario: message.data.current_scenario || null,
                batchName: message.data.batch_name || null,
                currentBatchIndex: batchIndex,
                projectId: message.data.project_id,
                messageId: message.data.message_id,
                liveFrame: null,
                scenarios: scenariosList,
                testPlan: updatedTestPlan.length > 0 ? updatedTestPlan : prev.testPlan,
              };
            });

          } else if (message.type === 'dev_live_test_action') {
            // Live test action update - just update the current action and scenario
            console.log('🎬 Live test action:', message.data.current_action, '| scenario:', message.data.current_scenario);
            setLiveTestState(prev => {
              // Mark the new current scenario as running in both scenarios and testPlan
              const updatedScenarios = prev.scenarios.map(scenario => {
                if (message.data.current_scenario && scenario.name === message.data.current_scenario) {
                  return { ...scenario, status: 'running' as const };
                }
                return scenario;
              });
              
              // Also update testPlan
              const updatedTestPlan = prev.testPlan.map((batch, bIdx) => {
                if (bIdx === prev.currentBatchIndex) {
                  return {
                    ...batch,
                    scenarios: batch.scenarios.map(s => {
                      if (message.data.current_scenario && s.name === message.data.current_scenario) {
                        return { ...s, status: 'running' as const };
                      }
                      return s;
                    }),
                  };
                }
                return batch;
              });
              
              return {
                ...prev,
                currentAction: message.data.current_action || '',
                currentScenario: message.data.current_scenario || prev.currentScenario,
                scenarios: updatedScenarios,
                testPlan: updatedTestPlan,
              };
            });

          } else if (message.type === 'dev_live_test_scenario_result') {
            // Scenario completed - update status based on backend result
            console.log('✅ Scenario result:', message.data);
            const { batch_index, scenario_index, status } = message.data;
            const newStatus = status === 'pass' ? 'passed' as const : 'failed' as const;
            
            setLiveTestState(prev => {
              // Update scenarios (legacy, current batch)
              const updatedScenarios = prev.scenarios.map((scenario, index) => {
                if (index === scenario_index) {
                  return { ...scenario, status: newStatus };
                }
                return scenario;
              });
              
              // Update testPlan
              const bIdx = batch_index ?? prev.currentBatchIndex;
              const updatedTestPlan = prev.testPlan.map((batch, idx) => {
                if (idx === bIdx) {
                  return {
                    ...batch,
                    scenarios: batch.scenarios.map((s, sIdx) => {
                      if (sIdx === scenario_index) {
                        return { ...s, status: newStatus };
                      }
                      return s;
                    }),
                  };
                }
                return batch;
              });
              
              return { ...prev, scenarios: updatedScenarios, testPlan: updatedTestPlan };
            });

          } else if (message.type === 'dev_live_test_ended') {
            // Live test recording has ended
            // Preserve isBatchInitializing state if it was set (next batch event may have arrived)
            // Keep testPlan for display after test ends
            console.log('🎬 Live test ended:', message.data);
            setLiveTestState(prev => ({
              ...prev,
              isLive: false,
              recordingPath: null,
              currentAction: '',
              currentScenario: null,
              batchName: null,
              liveFrame: null,
              // Keep testPlan and scenarios visible after test ends
            }));

          } else if (message.type === 'dev_live_frame') {
            // Live frame received for real-time preview
            const { frame, is_final } = message.data;
            if (frame && !is_final) {
              // Update the live frame for display
              setLiveTestState(prev => ({
                ...prev,
                liveFrame: frame,
              }));
            }
            // When stream ends, the frame will be null and is_final will be true
            // The dev_live_test_ended event handles clearing the state

          } else {
            // Catch-all for unhandled events
            console.log('🟡 UNHANDLED EVENT TYPE:', message.type, message.data);
          }
          
          // Handle loading/streaming states for other completion events
          if (message.type === 'dev_conversation_complete' || message.type === 'dev_message_end' || message.type === 'dev_text_end') {
            setIsLoading(false);
            setIsStreaming(false);
          }
        },
        onConnect: () => {
          console.log('✅ WebSocket connected for dev agent');
        },
        onDisconnect: () => {
          console.log('🔌 WebSocket disconnected for dev agent');
        },
        onError: () => {
          console.error('❌ WebSocket error for dev agent');
        }
      });
      console.log('✅ WebSocket connection initiated successfully');
    } catch (error) {
      console.error('❌ Error connecting to WebSocket:', error);
    }
    
    // Check for pending credential requests (for state persistence)
    if (threadId) {
      await checkForPendingCredentialRequest(threadId);
    }
    
    console.log('✅ selectThread completed successfully');
  }, [currentThreadId, webSocket, handleReconnection, onMessageUpdate, removeWaitingMessage, checkForPendingCredentialRequest]);

  // Stop conversation (for compatibility with existing code)
  const stopConversation = useCallback(async () => {
    if (!currentThreadId) return;

    // Spinner from the moment Stop is pressed, not from the answer: a run stopping on
    // another worker can end before the answer arrives, and its end must not go by
    // unnoticed (endStopping).
    isStoppingRef.current = true;
    setIsStopping(true);

    try {
      console.log('🛑 Stopping conversation:', currentThreadId);
      const response = await api.post(`/dev-agent/projects/${projectId}/conversations/${currentThreadId}/stop`);
      const stopStatus = response?.data?.status;

      if (stopStatus === 'stopping' || stopStatus === 'already_stopping') {
        // The run is on another backend worker, which ends it at its next check. Until
        // then it keeps showing as running; its own end events update the chat and turn
        // the spinner off, unless it already ended while this request was on its way.
        if (isStoppingRef.current) {
          stoppingTimeoutRef.current = window.setTimeout(endStopping, STOPPING_TIMEOUT_MS);
        }
        return;
      }

      // Stopped right away, or nothing was running.
      endStopping();

      // Reset UI state
      setIsLoading(false);
      setIsStreaming(false);
      setWaitingState('none');
      
      // Clear live test state when stopping
      setLiveTestState({
        isLive: false,
        isBatchInitializing: false,
        recordingPath: null,
        currentAction: '',
        currentScenario: null,
        batchName: null,
        currentBatchIndex: 0,
        projectId: null,
        messageId: null,
        liveFrame: null,
        scenarios: [],
        testPlan: [],
      });
      
      // Clear any active timeouts
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      
      // Remove thinking message and reload conversation to get final state
      removeWaitingMessage();
      isStreamingRef.current = false; // so the reload doesn't re-add the "thinking" placeholder
      await loadMessages(currentThreadId, true); // Force reload after stop

      console.log('✅ Conversation stopped and reloaded');
    } catch (error) {
      console.error('Error stopping conversation:', error);
      endStopping();
      // Still reset UI state even if stop call fails
      setIsLoading(false);
      setIsStreaming(false);
      setWaitingState('none');
      // Also clear live test state on error
      setLiveTestState({
        isLive: false,
        isBatchInitializing: false,
        recordingPath: null,
        currentAction: '',
        currentScenario: null,
        batchName: null,
        currentBatchIndex: 0,
        projectId: null,
        messageId: null,
        liveFrame: null,
        scenarios: [],
        testPlan: [],
      });
    }
  }, [currentThreadId]);

  // A stop in progress belongs to the thread it was pressed on.
  useEffect(() => () => endStopping(), [currentThreadId, endStopping]);

  // Add a thinking message to show the system is processing
  const addProcessingMessage = useCallback((message: string = 'Processing your request...') => {
    const thinkingMessage: Message = {
      id: 'agent-thinking',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isThinking: true,
      thinkingMessage: message
    };

    setMessages(prev => {
      // Remove any existing thinking message
      const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
      const updated = [...filtered, thinkingMessage];

      console.log('📝 ADD_PROCESSING_MESSAGE: Added processing message');
      onMessageUpdate?.(updated);
      return updated;
    });
  }, [onMessageUpdate]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
      }
      if (messageTimeoutRef.current) {
        window.clearTimeout(messageTimeoutRef.current);
      }
      webSocket.disconnect();
    };
  }, []);

  return {
    // State
    messages,
    currentThreadId,
    isLoading,
    isStreaming,
    isStopping,
    waitingState,
    isCheckingState,
    currentConversationState,
    hasMoreMessages,
    viewMode: chatView.mode,

    // Version control state
    viewingSegmentId,
    // Segment view ("View Original Chat") — its own paginated window
    viewingSegmentMessages: segmentWindow.messages,
    segmentViewMode: segmentWindow.viewMode,
    segmentHasMoreOlder: segmentWindow.hasMoreOlder,
    loadSegmentFrom: segmentWindow.loadFrom,
    loadSegmentOlder: segmentWindow.loadOlder,
    loadSegmentNewer: segmentWindow.loadNewer,
    revertStatus,
    setRevertStatus,
    
    // Live test streaming state
    liveTestState,
    
    // Actions
    selectThread,
    setCurrentThreadId,
    addProcessingMessage,
    loadMessages,
    loadMoreMessages,
    loadMessagesFrom,
    loadNewerMessages,
    stopConversation,
    resumeConversation,
    discardConversation,
    waitMore,
    
    // State setters (for special cases)
    setMessages,
    setIsLoading,
    setIsStreaming,

    // Version control actions
    loadSegmentMessages,
    clearViewingSegment,
    
    // WebSocket state
    socketState: webSocket.socketState,
    lastSocketEvent: webSocket.lastSocketEvent,
    isReconnecting: webSocket.isReconnecting,
    reconnectAttempt: webSocket.reconnectAttempt,
    
    // Credential management
    credentialCaptureRequest,
    submitCapturedCredentials,
    clearCredentialCaptureRequest: useCallback(() => {
      console.log('❌ Credential capture cancelled by user');
      setCredentialCaptureRequest(null);
      // Resume the 60-second timeout since user interaction is complete
      resumeWaitingTimeout();
    }, [resumeWaitingTimeout])
  };
}